import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/App";
import { supabase, createTimelineEvent } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DollarSign, Plus, ArrowLeft, Save, Loader2, ChevronDown, ChevronUp,
  Download, Trash2, FileText, Upload, Paperclip, AlertCircle, Pencil, Columns3,
} from "lucide-react";
import { toast } from "sonner";
import { OfferComparisonView } from "./OfferComparisonView";

const ALLOWED_EXTENSIONS = new Set(["pdf", "png", "jpg", "jpeg", "docx"]);
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const SIGNED_URL_EXPIRY = 300;

const EMPTY_FORM = {
  offer_label: "",
  buyer_name: "",
  buyers_agent: "",
  lender: "",
  purchase_price: "",
  earnest_money_deposit: "",
  pre_approval_received: "unknown",
  financing_type: "",
  appraisal_gap_coverage: "",
  loan_application_deadline: "",
  loan_approval_deadline: "",
  inspection_contingency: "",
  inspection_period_days: "",
  remedy_period_days: "",
  remedy_repair_terms: "",
  home_warranty: "",
  closing_date: "",
  possession_date: "",
  occupancy_agreement: "",
  offer_open_until: "",
  offer_open_until_time: "",
  items_to_remain: "",
};

/* --- Comma formatting helpers --- */
/** Format a raw numeric string with commas for display: "1000000" → "1,000,000" */
const addCommas = (val) => {
  if (!val && val !== 0) return "";
  const str = String(val).replace(/[^0-9]/g, "");
  if (!str) return "";
  return str.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/** Strip commas to get raw digits: "1,000,000" → "1000000" */
const stripCommas = (val) => String(val || "").replace(/,/g, "");

const formatCurrency = (val) => {
  const num = typeof val === "string" ? parseFloat(val) : val;
  if (isNaN(num) || num == null) return null;
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(num);
};

const formatDate = (val) => {
  if (!val) return null;
  return new Date(val + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/** Format an offer_open_until timestamp for display.
 *  Uses the `has_time` boolean to decide format — no sentinel guessing.
 *  Date-only → "Apr 15, 2026"  |  Date+time → "Apr 15, 11:59 PM" */
const formatDeadline = (val, hasTime) => {
  if (!val) return null;
  const d = new Date(val);
  if (isNaN(d.getTime())) return null;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  if (!hasTime) {
    return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
  }
  let h = d.getUTCHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${h}:${min} ${ampm}`;
};

/** Extract date part (YYYY-MM-DD) from a timestamp for the date input (UTC) */
const extractDatePart = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Extract time part (HH:MM) from a timestamp, or "" if no time was entered (UTC) */
const extractTimePart = (ts, hasTime) => {
  if (!ts || !hasTime) return "";
  const d = new Date(ts);
  const h = String(d.getUTCHours()).padStart(2, "0");
  const m = String(d.getUTCMinutes()).padStart(2, "0");
  return `${h}:${m}`;
};

/** Combine date string + optional time string into a UTC timestamp string.
 *  If no time → stores as midnight UTC. The boolean column controls display. */
const combineDateTime = (dateStr, timeStr) => {
  if (!dateStr) return null;
  const time = timeStr || "00:00";
  return `${dateStr}T${time}:00Z`;
};

const Label = ({ children }) => (
  <label className="text-xs font-medium text-muted-foreground mb-1 block">{children}</label>
);

const SectionHeading = ({ children }) => (
  <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider pt-4 pb-2 border-b border-border/50 mb-3 first:pt-0">{children}</h4>
);

export const OffersPanel = ({ transactionId }) => {
  const { user, displayName, profile } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableReady, setTableReady] = useState(true);
  const [view, setView] = useState("list"); // 'list' | 'form' | 'compare'
  const [editingOfferId, setEditingOfferId] = useState(null); // null = create mode, uuid = edit mode
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null); // {file_path, file_name} when editing
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);

  const isEditMode = !!editingOfferId;

  useEffect(() => {
    fetchOffers();
  }, [transactionId]);

  const isTableMissingError = (err) => {
    const msg = (err?.message || "").toLowerCase();
    const code = err?.code || "";
    return code === "42P01" || code === "PGRST204" || msg.includes("relation") && msg.includes("does not exist");
  };

  const fetchOffers = async () => {
    try {
      const { data, error } = await supabase
        .from("offers")
        .select("*")
        .eq("transaction_id", transactionId)
        .order("created_at", { ascending: false });

      if (error) {
        if (isTableMissingError(error)) setTableReady(false);
        else throw error;
      } else {
        setOffers(data || []);
      }
    } catch (err) {
      console.error("[Offers] Fetch error:", err);
      if (isTableMissingError(err)) setTableReady(false);
      else toast.error("Failed to load offers");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  /** Update a currency field: strip non-digits, reformat with commas */
  const updateCurrencyField = (key, rawInput) => {
    const digits = rawInput.replace(/[^0-9]/g, "");
    setForm((prev) => ({ ...prev, [key]: addCommas(digits) }));
  };

  const validateFile = (f) => {
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!ext || !ALLOWED_EXTENSIONS.has(ext)) return `File type ".${ext}" is not allowed. Accepted: PDF, PNG, JPG, DOCX.`;
    if (f.size > MAX_FILE_SIZE) return `File exceeds the 10 MB limit.`;
    return null;
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const err = validateFile(f);
    if (err) { toast.error(err); if (fileInputRef.current) fileInputRef.current.value = ""; return; }
    setFile(f);
    setExistingFile(null); // replacing existing file
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM });
    setFile(null);
    setExistingFile(null);
    setEditingOfferId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openCreateForm = () => {
    resetForm();
    setView("form");
  };

  const openEditForm = (offer) => {
    setEditingOfferId(offer.id);
    setForm({
      offer_label: offer.offer_label || "",
      buyer_name: offer.buyer_name || "",
      buyers_agent: offer.buyers_agent || "",
      lender: offer.lender || "",
      purchase_price: offer.purchase_price ? addCommas(String(Math.round(offer.purchase_price))) : "",
      earnest_money_deposit: offer.earnest_money_deposit ? addCommas(String(Math.round(offer.earnest_money_deposit))) : "",
      pre_approval_received: offer.pre_approval_received || "unknown",
      financing_type: offer.financing_type || "",
      appraisal_gap_coverage: offer.appraisal_gap_coverage || "",
      loan_application_deadline: offer.loan_application_deadline || "",
      loan_approval_deadline: offer.loan_approval_deadline || "",
      inspection_contingency: offer.inspection_contingency || "",
      inspection_period_days: offer.inspection_period_days != null ? String(offer.inspection_period_days) : "",
      remedy_period_days: offer.remedy_period_days != null ? String(offer.remedy_period_days) : "",
      remedy_repair_terms: offer.remedy_repair_terms || "",
      home_warranty: offer.home_warranty || "",
      closing_date: offer.closing_date || "",
      possession_date: offer.possession_date || "",
      occupancy_agreement: offer.occupancy_agreement || "",
      offer_open_until: extractDatePart(offer.offer_open_until),
      offer_open_until_time: extractTimePart(offer.offer_open_until, !!offer.offer_open_until_has_time),
      items_to_remain: offer.items_to_remain || "",
    });
    setFile(null);
    setExistingFile(offer.file_path ? { file_path: offer.file_path, file_name: offer.file_name } : null);
    setView("form");
  };

  const buildRecord = () => ({
    offer_label: form.offer_label.trim(),
    buyer_name: form.buyer_name || null,
    buyers_agent: form.buyers_agent || null,
    lender: form.lender || null,
    purchase_price: stripCommas(form.purchase_price) ? parseFloat(stripCommas(form.purchase_price)) : null,
    earnest_money_deposit: stripCommas(form.earnest_money_deposit) ? parseFloat(stripCommas(form.earnest_money_deposit)) : null,
    pre_approval_received: form.pre_approval_received || "unknown",
    financing_type: form.financing_type || null,
    appraisal_gap_coverage: form.appraisal_gap_coverage || null,
    loan_application_deadline: form.loan_application_deadline || null,
    loan_approval_deadline: form.loan_approval_deadline || null,
    inspection_contingency: form.inspection_contingency || null,
    inspection_period_days: form.inspection_period_days ? parseInt(form.inspection_period_days, 10) : null,
    remedy_period_days: form.remedy_period_days ? parseInt(form.remedy_period_days, 10) : null,
    remedy_repair_terms: form.remedy_repair_terms || null,
    home_warranty: form.home_warranty || null,
    closing_date: form.closing_date || null,
    possession_date: form.possession_date || null,
    occupancy_agreement: form.occupancy_agreement || null,
    offer_open_until: combineDateTime(form.offer_open_until, form.offer_open_until_time),
    offer_open_until_has_time: !!form.offer_open_until_time,
    items_to_remain: form.items_to_remain || null,
  });

  const handleSave = async () => {
    if (!form.offer_label.trim()) { toast.error("Offer Label is required"); return; }

    // Require document for NEW offers only
    if (!isEditMode && !file) {
      toast.error("Please attach the offer document before saving");
      return;
    }

    setSaving(true);

    try {
      let filePath = existingFile?.file_path || null;
      let fileName = existingFile?.file_name || null;

      // Upload new file if selected
      if (file) {
        const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
        const storagePath = `${transactionId}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage.from("documents").upload(storagePath, file, { contentType: file.type, upsert: false });
        if (upErr) throw upErr;

        // If replacing an existing file in edit mode, remove old one
        if (isEditMode && existingFile?.file_path) {
          await supabase.storage.from("documents").remove([existingFile.file_path]).catch(() => {});
        }

        filePath = storagePath;
        fileName = file.name;
      }

      const record = { ...buildRecord(), file_path: filePath, file_name: fileName };

      if (isEditMode) {
        // UPDATE existing offer
        const { error } = await supabase.from("offers").update(record).eq("id", editingOfferId);
        if (error) throw error;
        await createTimelineEvent(transactionId, "offer_updated", `Updated offer: ${form.offer_label.trim()}`, {
          offer_label: form.offer_label.trim(),
          purchase_price: record.purchase_price,
        });
        toast.success("Offer updated");
      } else {
        // INSERT new offer
        record.transaction_id = transactionId;
        record.created_by = user.id;
        const { error } = await supabase.from("offers").insert(record);
        if (error) throw error;
        await createTimelineEvent(transactionId, "offer_added", `New offer: ${form.offer_label.trim()}`, {
          offer_label: form.offer_label.trim(),
          purchase_price: record.purchase_price,
        });
        toast.success("Offer saved");
      }

      resetForm();
      setView("list");
      fetchOffers();
    } catch (err) {
      console.error("[Offers] Save error:", err);
      toast.error(err.message || "Failed to save offer");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (offer) => {
    if (!confirm(`Delete "${offer.offer_label}"? This cannot be undone.`)) return;
    setDeletingId(offer.id);
    try {
      if (offer.file_path) {
        await supabase.storage.from("documents").remove([offer.file_path]);
      }
      const { error } = await supabase.from("offers").delete().eq("id", offer.id);
      if (error) throw error;
      await createTimelineEvent(transactionId, "offer_deleted", `Deleted offer: ${offer.offer_label}`);
      toast.success("Offer deleted");
      setOffers((prev) => prev.filter((o) => o.id !== offer.id));
      if (expandedId === offer.id) setExpandedId(null);
    } catch (err) {
      console.error("[Offers] Delete error:", err);
      toast.error("Failed to delete offer");
    } finally {
      setDeletingId(null);
    }
  };

  const handleDownloadDoc = async (offer) => {
    if (!offer.file_path) return;
    try {
      const { data, error } = await supabase.storage.from("documents").createSignedUrl(offer.file_path, SIGNED_URL_EXPIRY);
      if (error) throw error;
      const a = document.createElement("a");
      a.href = data.signedUrl;
      a.download = offer.file_name || "document";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("[Offers] Download error:", err);
      toast.error("Failed to download document");
    }
  };

  // Has a document either from file picker or existing record
  const hasDocument = !!file || !!existingFile;

  // --- Not-ready state ---
  if (!tableReady) {
    return (
      <Card className="border-border/60 shadow-soft" data-testid="offers-panel">
        <CardContent className="py-12 text-center">
          <AlertCircle className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-sm font-medium text-foreground mb-1">Offers feature requires setup</p>
          <p className="text-xs text-muted-foreground max-w-md mx-auto">
            Run the <code className="px-1 py-0.5 bg-muted rounded text-xs">supabase_offers_migration.sql</code> migration in your Supabase SQL Editor to enable this feature.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="border-border/60 shadow-soft" data-testid="offers-panel">
        <CardContent className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </CardContent>
      </Card>
    );
  }

  // --- Form view (create + edit) ---
  if (view === "form") {
    return (
      <Card className="border-border/60 shadow-soft" data-testid="offers-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => { setView("list"); resetForm(); }} data-testid="offers-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <CardTitle className="font-heading text-base font-semibold">{isEditMode ? "Edit Offer" : "New Offer"}</CardTitle>
              <CardDescription className="text-sm">{isEditMode ? "Update the terms for this offer" : "Fill in the key terms for this offer or counter-offer"}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[540px] pr-3">
            <div className="space-y-0">
              {/* Basic Info */}
              <SectionHeading>Basic Info</SectionHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                <div className="sm:col-span-2">
                  <Label>Offer Label *</Label>
                  <Input placeholder='e.g. "Initial Offer" or "Counter #2"' value={form.offer_label} onChange={(e) => updateField("offer_label", e.target.value)} data-testid="offer-label-input" />
                </div>
                <div>
                  <Label>Buyer Name</Label>
                  <Input placeholder="Full name" value={form.buyer_name} onChange={(e) => updateField("buyer_name", e.target.value)} data-testid="offer-buyer-name" />
                </div>
                <div>
                  <Label>Buyer's Agent</Label>
                  <Input placeholder="Agent name" value={form.buyers_agent} onChange={(e) => updateField("buyers_agent", e.target.value)} data-testid="offer-buyers-agent" />
                </div>
                <div>
                  <Label>Lender</Label>
                  <Input placeholder="Lender name" value={form.lender} onChange={(e) => updateField("lender", e.target.value)} data-testid="offer-lender" />
                </div>
                <div>
                  <Label>Purchase Price</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <Input className="pl-7" placeholder="0" inputMode="numeric" value={form.purchase_price} onChange={(e) => updateCurrencyField("purchase_price", e.target.value)} data-testid="offer-purchase-price" />
                  </div>
                </div>
                <div>
                  <Label>Earnest Money Deposit</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                    <Input className="pl-7" placeholder="0" inputMode="numeric" value={form.earnest_money_deposit} onChange={(e) => updateCurrencyField("earnest_money_deposit", e.target.value)} data-testid="offer-earnest-money" />
                  </div>
                </div>
                <div>
                  <Label>Pre-Approval Received</Label>
                  <Select value={form.pre_approval_received} onValueChange={(v) => updateField("pre_approval_received", v)}>
                    <SelectTrigger data-testid="offer-pre-approval">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unknown">Unknown</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Financing & Appraisal */}
              <SectionHeading>Financing & Appraisal</SectionHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                <div>
                  <Label>Financing Type</Label>
                  <Select value={form.financing_type || "__none__"} onValueChange={(v) => updateField("financing_type", v === "__none__" ? "" : v)}>
                    <SelectTrigger data-testid="offer-financing-type">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not specified</SelectItem>
                      <SelectItem value="Conventional">Conventional</SelectItem>
                      <SelectItem value="FHA">FHA</SelectItem>
                      <SelectItem value="VA">VA</SelectItem>
                      <SelectItem value="USDA">USDA</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Appraisal Gap Coverage</Label>
                  <Input placeholder="e.g. Up to $15,000" value={form.appraisal_gap_coverage} onChange={(e) => updateField("appraisal_gap_coverage", e.target.value)} data-testid="offer-appraisal-gap" />
                </div>
                <div>
                  <Label>Loan Application Deadline</Label>
                  <Input type="date" value={form.loan_application_deadline} onChange={(e) => updateField("loan_application_deadline", e.target.value)} data-testid="offer-loan-app-deadline" />
                </div>
                <div>
                  <Label>Loan Approval Deadline</Label>
                  <Input type="date" value={form.loan_approval_deadline} onChange={(e) => updateField("loan_approval_deadline", e.target.value)} data-testid="offer-loan-approval-deadline" />
                </div>
              </div>

              {/* Inspection & Repair Terms */}
              <SectionHeading>Inspection & Repair Terms</SectionHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                <div>
                  <Label>Inspection Contingency</Label>
                  <Select value={form.inspection_contingency || "__none__"} onValueChange={(v) => updateField("inspection_contingency", v === "__none__" ? "" : v)}>
                    <SelectTrigger data-testid="offer-inspection">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not specified</SelectItem>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                      <SelectItem value="Waived">Waived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Inspection Period (days)</Label>
                  <div className="relative">
                    <Input placeholder="e.g. 10" inputMode="numeric" value={form.inspection_period_days} onChange={(e) => updateField("inspection_period_days", e.target.value.replace(/[^0-9]/g, ""))} data-testid="offer-inspection-period" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">days</span>
                  </div>
                </div>
                <div>
                  <Label>Remedy Period (days)</Label>
                  <div className="relative">
                    <Input placeholder="e.g. 3" inputMode="numeric" value={form.remedy_period_days} onChange={(e) => updateField("remedy_period_days", e.target.value.replace(/[^0-9]/g, ""))} data-testid="offer-remedy-period" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">days</span>
                  </div>
                </div>
                <div>
                  <Label>Home Warranty</Label>
                  <Select value={form.home_warranty || "__none__"} onValueChange={(v) => updateField("home_warranty", v === "__none__" ? "" : v)}>
                    <SelectTrigger data-testid="offer-home-warranty">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not specified</SelectItem>
                      <SelectItem value="Included">Included</SelectItem>
                      <SelectItem value="Not included">Not included</SelectItem>
                      <SelectItem value="Negotiable">Negotiable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label>Remedy / Repair Terms</Label>
                  <Textarea placeholder="Describe repair terms, limits, or caps..." value={form.remedy_repair_terms} onChange={(e) => updateField("remedy_repair_terms", e.target.value)} rows={2} data-testid="offer-remedy-terms" />
                </div>
              </div>

              {/* Timing & Possession */}
              <SectionHeading>Timing & Possession</SectionHeading>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                <div>
                  <Label>Closing Date</Label>
                  <Input type="date" value={form.closing_date} onChange={(e) => updateField("closing_date", e.target.value)} data-testid="offer-closing-date" />
                </div>
                <div>
                  <Label>Possession Date</Label>
                  <Input type="date" value={form.possession_date} onChange={(e) => updateField("possession_date", e.target.value)} data-testid="offer-possession-date" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Offer Open Until</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" value={form.offer_open_until} onChange={(e) => updateField("offer_open_until", e.target.value)} data-testid="offer-open-until" />
                    <div className="relative">
                      <Input type="time" value={form.offer_open_until_time} onChange={(e) => updateField("offer_open_until_time", e.target.value)} placeholder="" data-testid="offer-open-until-time" />
                      {!form.offer_open_until_time && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground/60 pointer-events-none">optional</span>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Occupancy Agreement</Label>
                  <Input placeholder="e.g. Buyer at closing" value={form.occupancy_agreement} onChange={(e) => updateField("occupancy_agreement", e.target.value)} data-testid="offer-occupancy" />
                </div>
              </div>

              {/* Included Property Items */}
              <SectionHeading>Included Property Items</SectionHeading>
              <div>
                <Label>Items to Remain</Label>
                <Textarea placeholder="List appliances, fixtures, or other items included..." value={form.items_to_remain} onChange={(e) => updateField("items_to_remain", e.target.value)} rows={2} data-testid="offer-items-to-remain" />
              </div>

              {/* Linked File */}
              <SectionHeading>Offer Document {!isEditMode && <span className="text-destructive">*</span>}</SectionHeading>
              <div>
                <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx" className="hidden" onChange={handleFileSelect} data-testid="offer-file-input" />
                {file ? (
                  <div className="flex items-center gap-2 p-2.5 rounded-md border border-border/60 bg-muted/30">
                    <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm truncate flex-1">{file.name}</span>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}>Remove</Button>
                  </div>
                ) : existingFile ? (
                  <div className="flex items-center gap-2 p-2.5 rounded-md border border-border/60 bg-muted/30">
                    <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                    <span className="text-sm truncate flex-1">{prettifyName(existingFile.file_name)}</span>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => fileInputRef.current?.click()} data-testid="offer-replace-doc-btn">Replace</Button>
                  </div>
                ) : (
                  <Button variant="outline" className={`w-full justify-start ${!isEditMode ? "border-destructive/40 text-muted-foreground" : "text-muted-foreground"}`} onClick={() => fileInputRef.current?.click()} data-testid="offer-upload-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    {isEditMode ? "Add offer document" : "Attach offer document (required)"}
                  </Button>
                )}
                <p className="text-[11px] text-muted-foreground mt-1.5">PDF, PNG, JPG, DOCX — max 10 MB</p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3 pt-5 pb-2">
                <Button onClick={handleSave} disabled={saving || !form.offer_label.trim()} data-testid="offer-save-btn">
                  {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />{isEditMode ? "Update Offer" : "Save Offer"}</>}
                </Button>
                <Button variant="ghost" onClick={() => { setView("list"); resetForm(); }}>Cancel</Button>
              </div>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    );
  }

  // --- Comparison view ---
  if (view === "compare") {
    return <OfferComparisonView offers={offers} onBack={() => setView("list")} />;
  }

  // --- List view ---
  return (
    <Card className="border-border/60 shadow-soft" data-testid="offers-panel">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Offers
            </CardTitle>
            <CardDescription className="text-sm">
              Review and compare offer terms for this transaction
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {offers.length >= 2 && (
              <Button variant="outline" className="h-9 text-sm" onClick={() => setView("compare")} data-testid="compare-offers-btn">
                <Columns3 className="w-4 h-4 mr-2" />
                Compare
              </Button>
            )}
            <Button className="btn-press rounded-md h-9 text-sm" onClick={openCreateForm} data-testid="add-offer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Offer
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {offers.length === 0 ? (
          <div className="text-center py-12" data-testid="offers-empty">
            <DollarSign className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No offers yet. Add an offer to capture and review key terms.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {offers.map((offer) => {
              const isExpanded = expandedId === offer.id;
              const isDeleting = deletingId === offer.id;
              return (
                <div
                  key={offer.id}
                  className={`rounded-lg border border-border/60 transition-colors ${isDeleting ? "opacity-50" : ""}`}
                  data-testid={`offer-card-${offer.id}`}
                >
                  {/* Card header / summary */}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold text-foreground truncate" data-testid={`offer-title-${offer.id}`}>{offer.offer_label}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Added {new Date(offer.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {offer.file_path && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" title="Download document" onClick={() => handleDownloadDoc(offer)} disabled={isDeleting} data-testid={`offer-download-${offer.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                        {offer.created_by === user?.id && (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit" onClick={() => openEditForm(offer)} disabled={isDeleting} data-testid={`offer-edit-${offer.id}`}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Delete" onClick={() => handleDelete(offer)} disabled={isDeleting} data-testid={`offer-delete-${offer.id}`}>
                              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </Button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Key fields grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2">
                      <SummaryField label="Buyer" value={offer.buyer_name} />
                      <SummaryField label="Buyer's Agent" value={offer.buyers_agent} />
                      <SummaryField label="Purchase Price" value={formatCurrency(offer.purchase_price)} highlight />
                      <SummaryField label="Earnest Money" value={formatCurrency(offer.earnest_money_deposit)} />
                      <SummaryField label="Lender" value={offer.lender} />
                      <SummaryField label="Closing Date" value={formatDate(offer.closing_date)} />
                      <SummaryField label="Offer Open Until" value={formatDeadline(offer.offer_open_until, !!offer.offer_open_until_has_time)} />
                      {offer.file_name && <SummaryField label="Document" value={<span className="flex items-center gap-1"><Paperclip className="w-3 h-3" />{prettifyName(offer.file_name)}</span>} />}
                    </div>
                  </div>

                  {/* Expand toggle */}
                  <button
                    className="w-full flex items-center justify-center gap-1.5 py-2 border-t border-border/40 text-xs text-muted-foreground hover:bg-muted/30 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : offer.id)}
                    data-testid={`offer-expand-${offer.id}`}
                  >
                    {isExpanded ? <><ChevronUp className="w-3.5 h-3.5" />Hide details</> : <><ChevronDown className="w-3.5 h-3.5" />View all details</>}
                  </button>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-border/40 bg-muted/20" data-testid={`offer-details-${offer.id}`}>
                      <div className="pt-3 space-y-3">
                        <DetailSection title="Financing & Appraisal">
                          <DetailRow label="Pre-Approval" value={offer.pre_approval_received === "yes" ? "Yes" : offer.pre_approval_received === "no" ? "No" : "Unknown"} />
                          <DetailRow label="Financing Type" value={offer.financing_type} />
                          <DetailRow label="Appraisal Gap Coverage" value={offer.appraisal_gap_coverage} />
                          <DetailRow label="Loan Application Deadline" value={formatDate(offer.loan_application_deadline)} />
                          <DetailRow label="Loan Approval Deadline" value={formatDate(offer.loan_approval_deadline)} />
                        </DetailSection>
                        <DetailSection title="Inspection & Repair">
                          <DetailRow label="Inspection Contingency" value={offer.inspection_contingency} />
                          <DetailRow label="Inspection Period" value={offer.inspection_period_days ? `${offer.inspection_period_days} days` : null} />
                          <DetailRow label="Remedy Period" value={offer.remedy_period_days ? `${offer.remedy_period_days} days` : null} />
                          <DetailRow label="Remedy / Repair Terms" value={offer.remedy_repair_terms} />
                          <DetailRow label="Home Warranty" value={offer.home_warranty} />
                        </DetailSection>
                        <DetailSection title="Timing & Possession">
                          <DetailRow label="Closing Date" value={formatDate(offer.closing_date)} />
                          <DetailRow label="Possession Date" value={formatDate(offer.possession_date)} />
                          <DetailRow label="Occupancy Agreement" value={offer.occupancy_agreement} />
                          <DetailRow label="Offer Open Until" value={formatDeadline(offer.offer_open_until, !!offer.offer_open_until_has_time)} />
                        </DetailSection>
                        {offer.items_to_remain && (
                          <DetailSection title="Included Items">
                            <p className="text-sm text-foreground whitespace-pre-wrap">{offer.items_to_remain}</p>
                          </DetailSection>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* --- Small presentational helpers --- */

const SummaryField = ({ label, value, highlight }) => (
  <div className="min-w-0">
    <p className="text-[11px] text-muted-foreground leading-tight">{label}</p>
    <p className={`text-sm truncate ${highlight ? "font-semibold text-foreground" : "text-foreground"}`}>
      {value || <span className="text-muted-foreground/50">—</span>}
    </p>
  </div>
);

const DetailSection = ({ title, children }) => (
  <div>
    <h5 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">{title}</h5>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">{children}</div>
  </div>
);

const DetailRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between py-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground text-right ml-2">{value}</span>
    </div>
  );
};

const prettifyName = (name) => {
  if (!name) return "Document";
  let base = name.replace(/\.[^.]+$/, "").replace(/[_\-.]+/g, " ").replace(/\s+/g, " ").trim();
  base = base.replace(/\b\w+/g, (w) => (w === w.toUpperCase() && w.length > 1 ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()));
  return base || "Document";
};
